import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useQuery, useMutation } from "react-query";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { X, Trash2 } from "lucide-react";
import GenericModal from "../../../GenericModal";
import { getUserDialerDetails, revokeUserDialerAccess } from "../../../../apis/phoneInventory";
import Flag from "../../../Flag";

export default function RemoveDialerAccessModal({
  isOpen,
  onClose,
  userToDelete,
  activeWorkspace,
  onSuccess,
}) {
  const [selectedNumbers, setSelectedNumbers] = useState(new Set());

  // Fetch user dialer details
  const {
    data: userDialerData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useQuery(
    ["get_user_dialer_details", userToDelete?.id, activeWorkspace?.id],
    () =>
      getUserDialerDetails({
        userId: userToDelete?.id,
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      }),
    {
      enabled: !!isOpen && !!userToDelete?.id && !!activeWorkspace?.id,
      retry: 2,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error(" Error fetching user dialer details:", error);
        toast.error("Failed to load user details. Please try again.");
      },
      onSuccess: (data) => {
        console.log("User dialer details loaded:", data);
        // console.log("Assigned numbers found:", data?.data?.assigned_numbers?.length || 0);
      },
    }
  );

  const userPhoneNumbers = userDialerData?.data?.assigned_numbers || userDialerData?.assigned_numbers || [];

  // Revoke user access mutation
  const revokeAccessMutation = useMutation(
    () =>
      revokeUserDialerAccess({
        data: {
          userId: userToDelete?.id,
          phone_numbers: [...selectedNumbers],
        },
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      }),
    {
      onSuccess: (data) => {
        // console.log(" User dialer access revoked successfully:", data);
        toast.success(`Dialer access revoked for ${userToDelete?.name}!`);
        onSuccess?.(); // Refresh the table
        onClose();
      },
    }
  );

  // Initialize all numbers as selected when user data is loaded
  useEffect(() => {
    if (isOpen && userPhoneNumbers.length > 0) {
      // console.log(
      //   " Initializing selected numbers:",
      //   userPhoneNumbers.map((phone) => phone.id)
      // );
      // setSelectedNumbers(new Set(userPhoneNumbers.map((phone) => phone.id)));
    } else {
      console.log("No phone numbers to initialize:", { isOpen, phoneCount: userPhoneNumbers.length });
    }
  }, [isOpen, userPhoneNumbers]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedNumbers(new Set());
    }
  }, [isOpen]);

  const handleNumberSelection = (phoneId, isSelected) => {
    setSelectedNumbers((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(phoneId);
      } else {
        newSet.delete(phoneId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedNumbers(
        new Set(userPhoneNumbers.filter((el) => el.assigned_only_to_this_user).map((phone) => phone.id))
      );
    } else {
      setSelectedNumbers(new Set());
    }
  };

  // Handle confirmation
  const handleRevokeAccess = () => {
    // console.log(" Revoking dialer access for user:", {
    //   userId: userToDelete?.id,
    //   userName: userToDelete?.name,
    //   workspaceId: activeWorkspace?.id,
    // });

    revokeAccessMutation.mutate();
  };

  // Get all selected status
  const allSelected = selectedNumbers.size === userPhoneNumbers.length && userPhoneNumbers.length > 0;

  // Loading states
  const isLoading = isLoadingUser || revokeAccessMutation.isLoading;

  return (
    <GenericModal isOpen={isOpen} closeModal={onClose} width="max-w-lg" dismiss={false}>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-100 rounded-md flex items-center justify-center p-3 mb-8">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-950">
                {selectedNumbers.size === 0 ? "Confirm user removal" : "Confirm number and user removal"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                A few numbers are assigned to this user. Confirm removal of these numbers or keep them to
                reassign to other users.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors mb-16">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="bg-gray-25 border border-[#E9EAEB] rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {userToDelete?.name?.charAt(0) || "U"}
              </span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-950">{userToDelete?.name || "Unknown User"}</h3>
              <p className="text-xs text-gray-500 font-normal">{userToDelete?.email || "No email"}</p>
            </div>
          </div>
        </div>

        {/* Selection Summary */}
        {userPhoneNumbers &&
          userPhoneNumbers.length > 0 &&
          userPhoneNumbers.filter((el) => el.assigned_only_to_this_user).length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="select-all" checked={allSelected} onCheckedChange={handleSelectAll} />
                <label htmlFor="select-all" className="text-sm font-medium text-purple-600">
                  {selectedNumbers.size} selected
                </label>
              </div>
            </div>
          )}

        {/* Phone Numbers List */}
        <div className="space-y-3 mb-6">
          {isLoadingUser ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-6 h-6 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
              <span className="ml-2 text-sm text-gray-500">Loading user details...</span>
            </div>
          ) : isErrorUser ? (
            <div className="text-center py-4">
              <span className="text-sm text-red-500">Failed to load user details</span>
            </div>
          ) : userPhoneNumbers.length === 0 ? (
            <div className="text-center py-4">
              <span className="text-sm text-gray-500">No phone numbers assigned</span>
            </div>
          ) : (
            userPhoneNumbers
              .filter((el) => el.assigned_only_to_this_user)
              .map((phone) => (
                <div key={phone.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={phone.id}
                    checked={selectedNumbers.has(phone.id)}
                    onCheckedChange={(checked) => handleNumberSelection(phone.id, checked)}
                    disabled={isLoading}
                  />
                  <div className="flex items-center space-x-2">
                    {/* Country Flag - Dynamic based on country */}
                    <span className="text-lg rounded-full border border-border overflow-hidden leading-none">
                      <Flag code={phone.country} squared />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-sm text-[#101828] font-semibold">
                        {phone.phone_number || phone.number}
                      </span>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-between space-x-3  ">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="bg-white w-4/12  text-[#414651] border border-[#D5D7DA] font-semibold px-[66px] shadow-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevokeAccess}
            disabled={isLoading || isLoadingUser || isErrorUser}
            variant="danger"
            className="flex-1"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {revokeAccessMutation.isLoading
              ? "Revoking..."
              : selectedNumbers.size === 0
                ? "Remove user access"
                : "Delete numbers and remove user"}
          </Button>
        </div>
      </div>
    </GenericModal>
  );
}
